import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams, useOutletContext } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Grid,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  Alert,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import BarcodeScannerComponent from 'react-qr-barcode-scanner';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import {
  useCreateSalesOrderMutation,
  useCreateHoldMutation,
  useGetSalesOrderQuery,
  useLazyGetCustomersQuery,
  useRecordSalesPaymentMutation,
  useUpdateSalesOrderStatusMutation,
  useHoldSalesOrderMutation,
  useUnholdSalesOrderMutation,
  useCreateCustomerMutation,
  useGetActiveRegisterSessionQuery,
  useOpenRegisterSessionMutation,
  useCloseRegisterSessionMutation,
} from '../../../store/api/salesApi';
import { useLazyGetProductsQuery, useGetWarrantyPeriodsQuery } from '../../../store/api/productsApi';
import { useGetPaymentAccountsQuery } from '../../../store/api/purchaseApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';
import { useGetLocationsQuery } from '../../../store/api/inventoryApi';
import { useGetUsersQuery } from '../../../store/api/userApi';
import { CustomerFormContent } from './CustomerForm';
import QuickProductDialog from '../../products/components/QuickProductDialog';
import ProductSearchOption from '../components/ProductSearchOption';
import { selectUser } from '../../../store/slices/authSlice';
import { useGetMyActiveTripQuery } from '../../../store/api/vanSalesApi';
import Chip from '@mui/material/Chip';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import EmailIcon from '@mui/icons-material/Email';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { formatItemDisplayName } from '../utils/formatItemDisplayName';
import { shareSalesOrderPdf, exportSalesOrderToPdf } from '../utils/exportSalesOrderPdf';
import { useGetEmailTemplatesQuery } from '../../../store/api/emailTemplateApi';
import { useSendInvoiceEmailMutation, useSendInvoiceWhatsappMutation } from '../../../store/api/salesApi';
import PinPromptDialog from '../../../shared/components/PinPromptDialog';

const DEFAULT_VAT_PERCENT = 5;

function SalesOrderForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isView = !!id && id !== 'new';
  const [viewUnlocked, setViewUnlocked] = useState(false);
  const [pinPromptOpen, setPinPromptOpen] = useState(true);

  const currentUser = useSelector(selectUser);

  const getCostCode = (costPrice, comp) => {
    if (!comp?.costPriceCodeMapping || comp.costPriceCodeMapping.length < 10) return null;
    if (costPrice == null) return null;
    const priceStr = String(Math.round(costPrice));
    let code = '';
    const mapping = comp.costPriceCodeMapping.toUpperCase();
    for (let char of priceStr) {
      if (char >= '0' && char <= '9') {
        code += mapping[parseInt(char, 10)];
      }
    }
    return code;
  };

  const outletContext = useOutletContext();
  const setNavigationBlocked = outletContext?.setNavigationBlocked;

  const [createSalesOrder] = useCreateSalesOrderMutation();
  const [createHold] = useCreateHoldMutation();
  const [updateSalesOrderStatus] = useUpdateSalesOrderStatusMutation();
  const [holdSalesOrder] = useHoldSalesOrderMutation();
  const [unholdSalesOrder] = useUnholdSalesOrderMutation();
  const [createCustomer] = useCreateCustomerMutation();

  const handleCreateCashCustomer = async () => {
    try {
      const created = await createCustomer({
        name: 'Cash Customer',
        phone: '0000000000',
        email: 'cash@example.com',
      }).unwrap();
      if (created?.id) {
        setCustomerSearchOptions(prev => [created, ...prev]);
        setFormData(prev => ({ ...prev, customerId: created.id }));
      }
    } catch (err) {
      console.error(err);
      alert(err?.data?.message || 'Failed to create Cash Customer');
    }
  };

  const [triggerCustomerSearch, { data: customerSearchData, isFetching: searchingCustomers }] = useLazyGetCustomersQuery();
  const [customerSearchOptions, setCustomerSearchOptions] = useState([]);
  const [triggerProductSearch, { data: productSearchData, isFetching: searchingProducts }] =
    useLazyGetProductsQuery();
  const { data: company } = useGetCurrentCompanyQuery();
  const { data: locations } = useGetLocationsQuery();
  const { data: usersData } = useGetUsersQuery();
  const { data: paymentAccounts } = useGetPaymentAccountsQuery();
  const { data: warrantyPeriodsData } = useGetWarrantyPeriodsQuery();
  const userList = Array.isArray(usersData) ? usersData : usersData?.users ?? [];
  const { data: existingOrder, isLoading: loadingOrder } = useGetSalesOrderQuery(id, {
    skip: !isView,
  });
  const { data: activeVanTrip } = useGetMyActiveTripQuery(undefined, { skip: isView });

  const vanMode = !isView && activeVanTrip && activeVanTrip.id;
  const vanLocationId = activeVanTrip?.van?.locationId || activeVanTrip?.van?.location?.id;
  const vanName = activeVanTrip?.van?.name;
  const vanPlate = activeVanTrip?.van?.plateNumber;
  const vanTripNumber = activeVanTrip?.tripNumber;

  const currency = company?.currency || 'USD';
  const vatIncluded = company?.salesVatIncluded !== false;
  const defaultVat = Number(company?.vatRate) || DEFAULT_VAT_PERCENT;
  const itemNameFormat = company?.printItemNameFormat || 'name_sku';

  const previewInvoiceNumber = (() => {
    const prefix = String(company?.invoicePrefix || 'INV').trim() || 'INV';
    const nextNum = Number(company?.invoiceNextNumber || 1) || 1;
    const paddingRaw = Number(company?.invoiceNumberPadding);
    const padding = Number.isInteger(paddingRaw) ? Math.min(12, Math.max(0, paddingRaw)) : 6;
    const numberPart = String(Math.max(1, nextNum)).padStart(padding, '0');
    return `${prefix}-${numberPart}`;
  })();

  const [formData, setFormData] = useState(() => ({
    customerId: '',
    invoiceNumber: '',
    invoiceDate: new Date().toISOString().split('T')[0],
    orderDate: new Date().toISOString().split('T')[0],
    salesType: 'cash',
    creditDays: '',
    salesPersonId: currentUser?.id || '',
    locationId: '',
    notes: '',
    roundOff: 0,
  }));

  const creditDaysOptions = (company?.creditDaysOptions || '7,14,30')
    .split(',')
    .map((s) => parseInt(s.trim(), 10))
    .filter((n) => Number.isInteger(n) && n > 0);

  const [items, setItems] = useState([]);
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
  const [quickProductOpen, setQuickProductOpen] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [printCheckOpen, setPrintCheckOpen] = useState(false);
  const [activeSerialIndex, setActiveSerialIndex] = useState(null);
  const [activeSerialScanIndex, setActiveSerialScanIndex] = useState(null);
  const [scanOpen, setScanOpen] = useState(false);
  const [scanError, setScanError] = useState('');
  const [pendingBarcode, setPendingBarcode] = useState('');
  const [duplicateDialog, setDuplicateDialog] = useState({
    open: false,
    existingIndex: -1,
    product: null,
  });
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState('');
  const [selectedEmailTemplate, setSelectedEmailTemplate] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);

  const [whatsappDialogOpen, setWhatsappDialogOpen] = useState(false);
  const [whatsappPhone, setWhatsappPhone] = useState('');
  const [whatsappLoading, setWhatsappLoading] = useState(false);

  const { data: emailTemplates } = useGetEmailTemplatesQuery();
  const [sendInvoiceEmail] = useSendInvoiceEmailMutation();
  const [sendInvoiceWhatsapp] = useSendInvoiceWhatsappMutation();

  useEffect(() => {
    if (existingOrder?.customer?.email) {
      setEmailRecipient(existingOrder.customer.email);
    }
    if (existingOrder?.customer?.phone) {
      setWhatsappPhone(existingOrder.customer.phone);
    }
  }, [existingOrder]);

  const handleSendEmail = async () => {
    if (!emailRecipient) {
      alert('Recipient email is required');
      return;
    }
    setEmailLoading(true);
    try {
      // ── Generate PDF (same as print template) in browser, in memory ──
      let pdfBase64 = undefined;
      if (existingOrder) {
        try {
          const arrayBuffer = await exportSalesOrderToPdf(existingOrder, company, {
            save: false,
            itemFormat: company?.printItemNameFormat || 'name_sku',
            filename: (existingOrder.invoiceNumber || existingOrder.orderNumber || id),
          });
          if (arrayBuffer) {
            // Convert ArrayBuffer → Base64 string
            const uint8 = new Uint8Array(arrayBuffer);
            let binary = '';
            for (let i = 0; i < uint8.length; i++) binary += String.fromCharCode(uint8[i]);
            pdfBase64 = btoa(binary);
          }
        } catch (pdfErr) {
          console.warn('PDF generation for email failed, backend will generate fallback:', pdfErr);
        }
      }

      await sendInvoiceEmail({
        id,
        email: emailRecipient,
        templateId: selectedEmailTemplate || undefined,
        pdfBase64,  // ← null/undefined if generation failed → backend uses pdfkit
      }).unwrap();
      alert('Email sent successfully with PDF attachment!');
      setEmailDialogOpen(false);
    } catch (err) {
      console.error(err);
      alert(err?.data?.message || 'Failed to send email');
    } finally {
      setEmailLoading(false);
    }
  };

  const handleSendWhatsapp = async () => {
    if (!whatsappPhone) {
      alert('Recipient phone number is required');
      return;
    }
    setWhatsappLoading(true);
    try {
      let pdfBase64 = undefined;
      if (existingOrder) {
        try {
          const arrayBuffer = await exportSalesOrderToPdf(existingOrder, company, {
            save: false,
            itemFormat: company?.printItemNameFormat || 'name_sku',
            filename: (existingOrder.invoiceNumber || existingOrder.orderNumber || id),
          });
          if (arrayBuffer) {
            const uint8 = new Uint8Array(arrayBuffer);
            let binary = '';
            for (let i = 0; i < uint8.length; i++) binary += String.fromCharCode(uint8[i]);
            pdfBase64 = btoa(binary);
          }
        } catch (pdfErr) {
          console.warn('PDF generation for WhatsApp failed, backend will generate fallback:', pdfErr);
        }
      }

      const res = await sendInvoiceWhatsapp({
        id,
        phone: whatsappPhone,
        pdfBase64,
      }).unwrap();

      if (res.waLink) {
        // If API is not configured, open the wa.me link in a new tab
        window.open(res.waLink, '_blank');
        alert('WhatsApp API not configured. A sharing link has been opened instead.');
      } else {
        alert('WhatsApp message sent successfully!');
      }
      setWhatsappDialogOpen(false);
    } catch (err) {
      console.error(err);
      alert(err?.data?.message || 'Failed to send WhatsApp message');
    } finally {
      setWhatsappLoading(false);
    }
  };

  useEffect(() => {
    if (setNavigationBlocked && !isView) {
      setNavigationBlocked(items.length > 0);
    }
    if (isView) return;
    const handleBeforeUnload = (e) => {
      if (items.length > 0) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.history.pushState({ isForm: true }, '', window.location.href);
    const handlePopState = (e) => {
      if (items.length > 0) {
        window.history.pushState({ isForm: true }, '', window.location.href);
        setCancelDialogOpen(true);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
      if (setNavigationBlocked) setNavigationBlocked(false);
    };
  }, [items, isView, setNavigationBlocked]);
  const [payments, setPayments] = useState(
    !!id && id !== 'new'
      ? []
      : [
        {
          method: 'cash',
          paymentAccountId: '',
          referenceNo: '',
          amount: '',
        },
      ]
  );
  const [autoPaymentAmount, setAutoPaymentAmount] = useState(true);
  const [billDiscount, setBillDiscount] = useState(0);

  const resetForm = () => {
    setFormData({
      customerId: company?.defaultCustomerId || '',
      invoiceNumber: '',
      invoiceDate: new Date().toISOString().split('T')[0],
      orderDate: new Date().toISOString().split('T')[0],
      salesType: 'cash',
      creditDays: '',
      salesPersonId: currentUser?.id || '',
      locationId: vanMode && vanLocationId ? vanLocationId : '',
      notes: '',
      roundOff: 0,
    });
    setItems([]);
    setPayments([
      {
        method: 'cash',
        paymentAccountId: '',
        referenceNo: '',
        amount: '',
      },
    ]);
    setAutoPaymentAmount(true);
    setBillDiscount(0);
    setProductSearch('');
    setPendingBarcode('');
  };

  const submitModeRef = useRef('save'); // 'save' | 'save-print' | 'quotation' - read in handleSubmit
  const [receivePaymentOpen, setReceivePaymentOpen] = useState(false);
  const [receivePaymentForm, setReceivePaymentForm] = useState({
    paymentDate: new Date().toISOString().split('T')[0],
    amount: '',
    paymentMethod: 'cash',
    paymentAccountId: '',
    referenceNo: '',
    notes: '',
  });
  const [recordSalesPayment, { isLoading: recordingPayment }] = useRecordSalesPaymentMutation();

  const [registerOpen, setRegisterOpen] = useState(true);
  const [openRegisterDialogOpen, setOpenRegisterDialogOpen] = useState(false);
  const [closeRegisterDialogOpen, setCloseRegisterDialogOpen] = useState(false);
  const [closingPettyCashInput, setClosingPettyCashInput] = useState('');
  const [openingBalanceInput, setOpeningBalanceInput] = useState('');
  const [closingBalanceInput, setClosingBalanceInput] = useState('');
  const [registerSession, setRegisterSession] = useState(null);

  const { data: dbSession, refetch: refetchSession, isUninitialized: sessionUninitialized, isLoading: sessionLoading } = useGetActiveRegisterSessionQuery(undefined, { skip: isView || company?.enableSalesSession !== true });
  const [openRegisterSessionApi] = useOpenRegisterSessionMutation();
  const [closeRegisterSessionApi] = useCloseRegisterSessionMutation();

  useEffect(() => {
    if (isView) return;
    if (!company) return;
    
    if (company.enableSalesSession !== true) {
      setRegisterOpen(true);
      setOpenRegisterDialogOpen(false);
      return;
    }

    if (sessionLoading || sessionUninitialized) return;

    if (dbSession) {
      setRegisterSession(dbSession);
      setRegisterOpen(true);
      setOpenRegisterDialogOpen(false);
    } else {
      setRegisterSession(null);
      setRegisterOpen(false);
      setOpenRegisterDialogOpen(true);
    }
  }, [isView, company, dbSession, sessionLoading, sessionUninitialized]);

  const handleOpenRegister = async () => {
    try {
      const openingAmt = parseFloat(openingBalanceInput) || 0;
      await openRegisterSessionApi({ openingBalance: openingAmt }).unwrap();
      if (!sessionUninitialized) refetchSession();
    } catch (err) {
      alert(err?.data?.message || 'Failed to open register session');
    }
  };

  const handleCloseRegister = async () => {
    try {
      const actualCash = parseFloat(closingBalanceInput) || 0;
      const pettyCash = parseFloat(closingPettyCashInput) || 0;
      const expected = (Number(registerSession?.openingBalance) || 0) + (Number(registerSession?.salesTotal) || 0);
      const transferAmount = Math.max(0, actualCash - pettyCash);
      const discrepancy = actualCash - expected;

      let reportMsg = `--- Shift Closed Successfully ---\n`;
      reportMsg += `Shift Opened At: ${registerSession?.openedAt ? new Date(registerSession.openedAt).toLocaleString() : ''}\n`;
      reportMsg += `Starting Cash: ${Number(registerSession?.openingBalance || 0).toFixed(2)} ${currency}\n`;
      reportMsg += `Sales processed: ${registerSession?.salesCount || 0} (${Number(registerSession?.salesTotal || 0).toFixed(2)} ${currency})\n`;
      reportMsg += `Expected Closing Cash: ${expected.toFixed(2)} ${currency}\n`;
      reportMsg += `Actual Cash Counted: ${actualCash.toFixed(2)} ${currency}\n`;
      reportMsg += `Petty Cash Kept in Drawer: ${pettyCash.toFixed(2)} ${currency}\n`;
      reportMsg += `Net Cash Transferred to Safe: ${transferAmount.toFixed(2)} ${currency}\n`;
      reportMsg += `Discrepancy: ${discrepancy >= 0 ? '+' : ''}${discrepancy.toFixed(2)} ${currency} (${discrepancy === 0 ? 'Balanced' : (discrepancy > 0 ? 'Surplus' : 'Shortage')})`;

      await closeRegisterSessionApi({ closingBalance: actualCash, closingPettyCash: pettyCash }).unwrap();
      setRegisterSession(null);
      setRegisterOpen(false);
      setCloseRegisterDialogOpen(false);
      setOpeningBalanceInput('');
      setClosingBalanceInput('');
      setClosingPettyCashInput('');
      setOpenRegisterDialogOpen(true);
      alert(reportMsg);
      if (!sessionUninitialized) refetchSession();
    } catch (err) {
      alert(err?.data?.message || 'Failed to close register session');
    }
  };

  useEffect(() => {
    if (customerSearchData) {
      const data = customerSearchData.data || (Array.isArray(customerSearchData) ? customerSearchData : []);
      setCustomerSearchOptions(prevOptions => {
        // Find if current selection is missing from new data
        const currentId = formData.customerId;
        const currentInNew = data.find(c => c.id === currentId);
        const currentInOld = prevOptions.find(c => c.id === currentId);

        if (currentId && !currentInNew && currentInOld) {
          return [currentInOld, ...data];
        }
        return data;
      });
    }
  }, [customerSearchData, formData.customerId]);

  useEffect(() => {
    if (existingOrder?.customer && !customerSearchOptions.find(c => c.id === existingOrder.customer.id)) {
      setCustomerSearchOptions(prev => [existingOrder.customer, ...prev]);
    }
  }, [existingOrder, customerSearchOptions]);
  useEffect(() => {
    if (!isView && company?.defaultCustomerId) {
      if (!formData.customerId) {
        setFormData(prev => ({ ...prev, customerId: company.defaultCustomerId }));
      }
      triggerCustomerSearch({ limit: 100 });
    }
  }, [company, isView, triggerCustomerSearch]);

  const selectedCustomer = customerSearchOptions.find((c) => c.id === formData.customerId) || null;

  useEffect(() => {
    if (!scanOpen) {
      setScanError('');
      setPendingBarcode('');
    }
  }, [scanOpen]);

  const handleAddProductRow = (product) => {
    if (!product) return;
    setItems((prev) => {
      const existingIndex = prev.findIndex((it) => String(it.productId) === String(product.id));
      if (existingIndex !== -1) {
        setDuplicateDialog({ open: true, existingIndex, product });
        const behavior = company?.barcodeAfterScanBehavior || 'search';
        if (behavior === 'quantity') {
          setTimeout(() => {
            const el = document.getElementById(`qty-input-${existingIndex}`);
            if (el) {
              el.focus();
              el.select();
            }
          }, 100);
        } else {
          setTimeout(() => {
            const el = document.getElementById('product-search-input');
            if (el) {
              el.focus();
              el.select();
            }
          }, 100);
        }
        return prev;
      }
      const newIndex = prev.length;
      const behavior = company?.barcodeAfterScanBehavior || 'search';
      if (behavior === 'quantity') {
        setTimeout(() => {
          const el = document.getElementById(`qty-input-${newIndex}`);
          if (el) {
            el.focus();
            el.select();
          }
        }, 100);
      } else {
        setTimeout(() => {
          const el = document.getElementById('product-search-input');
          if (el) {
            el.focus();
            el.select();
          }
        }, 100);
      }
      return [
        ...prev,
        {
          productId: product.id,
          productName: product.name,
          productSku: product.sku,
          productBrand: product.brand,
          productBarcode: product.barcode,
          maxDiscountType:
            product.maxDiscountType === 'percent' || product.maxDiscountType === 'amount'
              ? product.maxDiscountType
              : undefined,
          maxDiscountPercent: product.maxDiscountPercent != null ? product.maxDiscountPercent : undefined,
          maxDiscountAmount: product.maxDiscountAmount != null ? product.maxDiscountAmount : undefined,
          quantity: 1,
          unit: product.baseUnit || 'pcs',
          unitPrice: (vatIncluded && defaultVat > 0)
            ? Number(((product.sellingPrice || 0) / (1 + defaultVat / 100)).toFixed(company?.currencyDecimals ?? 2))
            : (product.sellingPrice ?? 0),
          discount: 0,
          vatPercent: defaultVat,
          costPrice: product.costPrice ?? 0,
          isReturn: false,
          isDamaged: false,
          damageReason: '',
          productType: product.type || 'product',
          hasWarranty: !!product.hasWarranty,
          warrantyPeriodId: product.warrantyPeriodId || null,
          serialNumbers: [],
          serviceData: product.type === 'service' ? { imei: '', deviceModel: '', complains: '' } : null,
        },
      ];
    });
  };

  useEffect(() => {
    if (!pendingBarcode) return;
    const list = productSearchData?.products || [];
    if (!Array.isArray(list) || list.length === 0) return;

    const code = String(pendingBarcode).trim();
    if (!code) return;

    const match = list.find((p) => String(p?.barcode || '').trim() === code);
    if (match) {
      handleAddProductRow(match);
      setProductSearch('');
      setPendingBarcode('');
      setScanOpen(false);
    }
  }, [pendingBarcode, productSearchData, handleAddProductRow]);

  useEffect(() => {
    const val = String(productSearch).trim();
    if (!val || val.length < 3) return;
    if (searchingProducts) return;
    const list = productSearchData?.products || [];
    if (list.length === 0) return;

    const match = list.find((p) =>
      String(p?.barcode || '').trim() === val ||
      String(p?.sku || '').trim() === val
    );

    if (match) {
      handleAddProductRow(match);
      setProductSearch('');
    }
  }, [productSearchData, productSearch, searchingProducts]);

  const handleRemoveItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const toNum = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

  const computeLine = (item) => {
    const isRet = !!item.isReturn;
    const qty = toNum(item.quantity);
    const price = toNum(item.unitPrice);
    const discount = toNum(item.discount);
    const vatPct = toNum(item.vatPercent);
    const lineSubtotal = Math.max(0, qty * price - discount);
    let vatAmount = 0;
    if (vatPct > 0) {
      vatAmount = lineSubtotal * (vatPct / 100);
    }
    const lineTotal = lineSubtotal + vatAmount;

    const dec = typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2;
    const multiplier = isRet ? -1 : 1;

    return {
      vatAmount: Math.round((vatAmount) * Math.pow(10, dec)) / Math.pow(10, dec),
      lineTotal: Math.round((lineTotal) * Math.pow(10, dec)) / Math.pow(10, dec),
      lineTotalMixed: Math.round((lineTotal * multiplier) * Math.pow(10, dec)) / Math.pow(10, dec),
      lineSubtotal,
    };
  };

  const orderSubtotal = items.reduce(
    (sum, item) => sum + (item.isReturn ? -1 : 1) * toNum(item.quantity) * toNum(item.unitPrice),
    0
  );
  const lineDiscountTotal = items.reduce((sum, item) => sum + (item.isReturn ? -1 : 1) * toNum(item.discount), 0);
  const orderVatTotal = items.reduce((sum, item) => sum + (item.isReturn ? -1 : 1) * computeLine(item).vatAmount, 0);

  // Actual base bill amount before bill-level discount and round-off
  const baseBillAmount = (orderSubtotal - lineDiscountTotal) + orderVatTotal;

  const orderDiscountTotal = lineDiscountTotal + toNum(billDiscount);
  const netOrder = orderSubtotal - orderDiscountTotal;
  const grandTotal = netOrder + orderVatTotal;
  const roundOff = toNum(formData.roundOff);
  const totalAmount = grandTotal + roundOff;

  const [editableTotalAmount, setEditableTotalAmount] = useState(null);

  const handleTotalAmountChange = (val) => {
    setEditableTotalAmount(val);
    if (val === '' || val === null || val === undefined) {
      setBillDiscount('');
      setFormData((prev) => ({ ...prev, roundOff: 0 }));
      return;
    }

    const enteredAmount = parseFloat(val);
    if (isNaN(enteredAmount)) {
      return;
    }

    const dec = typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2;
    const diff = Math.round((enteredAmount - baseBillAmount) * Math.pow(10, dec)) / Math.pow(10, dec);

    if (diff < 0) {
      // If amount is LESS than actual bill amount -> Add difference as discount
      const discountDiff = Math.round(Math.abs(diff) * Math.pow(10, dec)) / Math.pow(10, dec);
      setBillDiscount(discountDiff > 0 ? discountDiff : '');
      setFormData((prev) => ({ ...prev, roundOff: 0 }));
    } else if (diff > 0) {
      // If amount is GREATER than actual bill amount -> Add difference as round off
      setBillDiscount('');
      setFormData((prev) => ({ ...prev, roundOff: diff }));
    } else {
      // Exact match
      setBillDiscount('');
      setFormData((prev) => ({ ...prev, roundOff: 0 }));
    }
  };

  const paidTotal = payments.reduce(
    (sum, p) => sum + (Number.isFinite(Number(p.amount)) ? Number(p.amount) : 0),
    0
  );
  const remaining = totalAmount - paidTotal;
  const change = paidTotal - totalAmount;

  // If creating a new order and there is a logged-in user, default them as sales person
  useEffect(() => {
    if (!isView && currentUser?.id && !formData.salesPersonId) {
      setFormData((prev) => ({ ...prev, salesPersonId: currentUser.id }));
    }
  }, [isView, currentUser, formData.salesPersonId]);

  // Van mode: auto-set locationId and salesType to cash when user has active van trip
  useEffect(() => {
    if (vanMode && vanLocationId && !isView) {
      setFormData((prev) => ({
        ...prev,
        locationId: vanLocationId,
        salesType: 'cash',
      }));
    }
  }, [vanMode, vanLocationId, isView]);

  useEffect(() => {
    if (!isView && !vanMode && locations?.length && !formData.locationId) {
      const mainWarehouse = locations.find((l) => l.name.toLowerCase().includes('main warehouse'));
      if (mainWarehouse) {
        setFormData((prev) => ({ ...prev, locationId: mainWarehouse.id }));
      } else if (locations.length > 0) {
        setFormData((prev) => ({ ...prev, locationId: locations[0].id }));
      }
    }
  }, [locations, vanMode, isView]);

  useEffect(() => {
    if (existingOrder && isView) {
      setFormData({
        customerId: existingOrder.customerId || '',
        invoiceNumber: existingOrder.invoiceNumber || '',
        invoiceDate: existingOrder.invoiceDate
          ? existingOrder.invoiceDate.substring(0, 10)
          : formData.invoiceDate,
        orderDate: existingOrder.orderDate
          ? existingOrder.orderDate.substring(0, 10)
          : formData.orderDate,
        salesType: existingOrder.salesType || 'cash',
        creditDays: existingOrder.creditDays != null ? String(existingOrder.creditDays) : '',
        salesPersonId: existingOrder.salesPersonId || '',
        locationId: existingOrder.locationId || '',
        notes: existingOrder.notes || '',
        roundOff: existingOrder.roundOff || 0,
      });
      const mappedItems = (existingOrder.items || []).map((it) => ({
        productId: it.productId,
        productName: it.product?.name,
        productSku: it.product?.sku,
        productBrand: it.product?.brand,
        productBarcode: it.product?.barcode,
        maxDiscountType: it.product?.maxDiscountType === 'percent' || it.product?.maxDiscountType === 'amount' ? it.product.maxDiscountType : undefined,
        maxDiscountPercent: it.product?.maxDiscountPercent != null ? it.product.maxDiscountPercent : undefined,
        maxDiscountAmount: it.product?.maxDiscountAmount != null ? it.product.maxDiscountAmount : undefined,
        quantity: it.quantity,
        unit: it.unit || 'pcs',
        unitPrice: (vatIncluded && (toNum(it.vatPercent) || defaultVat) > 0)
          ? Number((it.unitPrice / (1 + (toNum(it.vatPercent) || defaultVat) / 100)).toFixed(company?.currencyDecimals ?? 2))
          : it.unitPrice,
        discount: toNum(it.discount),
        vatPercent: toNum(it.vatPercent) || defaultVat,
        batchNo: it.batchNo || '',
        expiryDate: it.expiryDate ? it.expiryDate.substring(0, 10) : '',
        costPrice: it.product?.costPrice ?? 0,
        isReturn: !!it.isReturn,
        isDamaged: !!it.isDamaged,
        damageReason: it.damageReason || '',
        productType: it.product?.type || 'product',
        serviceData: it.serviceData ? (typeof it.serviceData === 'string' ? JSON.parse(it.serviceData) : it.serviceData) : null,
        hasWarranty: !!it.hasWarranty,
        warrantyPeriodId: it.warrantyPeriodId || null,
        serialNumbers: typeof it.serialNumbers === 'string' ? JSON.parse(it.serialNumbers || '[]') : (it.serialNumbers || []),
      }));
      setItems(mappedItems);
      const lineDiscountTotal = mappedItems.reduce((sum, it) => sum + toNum(it.discount), 0);
      const orderDiscount = toNum(existingOrder.discountTotal);
      const extra = orderDiscount - lineDiscountTotal;
      setBillDiscount(extra > 0 ? extra : 0);
      const paid = toNum(existingOrder.paidAmount);
      if (paid > 0) {
        setPayments([
          { method: 'cash', paymentAccountId: '', referenceNo: '', amount: paid },
        ]);
        setAutoPaymentAmount(false);
      }
    }
  }, [existingOrder, isView]);

  // For new sales, while the user hasn't manually edited payments,
  // keep the single payment row synced to the full grand total (or empty for credit).
  useEffect(() => {
    if (
      !isView &&
      payments.length === 1 &&
      autoPaymentAmount
    ) {
      const target = formData.salesType === 'cash'
        ? totalAmount.toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))
        : '';
      
      if (payments[0].amount !== target) {
        const next = [...payments];
        next[0] = { ...next[0], amount: target };
        setPayments(next);
      }
    }
  }, [totalAmount, formData.salesType, isView, payments, autoPaymentAmount, company]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isView) {
      navigate('/sales');
      return;
    }
    if (company?.enableSalesSession === true && !registerOpen) {
      alert('Please Open the Register Session first before creating sales.');
      setOpenRegisterDialogOpen(true);
      return;
    }
    const mode = submitModeRef.current;
    submitModeRef.current = 'save';
    const isCreateAndHold = mode === 'save-hold';
    if (!isCreateAndHold) {
      if (!formData.customerId) {
        alert('Customer is required');
        return;
      }
      if (!formData.invoiceDate) {
        alert('Invoice Date is required');
        return;
      }
      if (!items.length) {
        alert('Please add at least one item');
        return;
      }
      if (items.some((i) => !i.productId)) {
        alert('Please select a product for all items');
        return;
      }
      const paidTotal = payments.reduce((sum, p) => sum + toNum(p.amount), 0);
      if (formData.salesType === 'cash' && paidTotal + 0.0001 < totalAmount) {
        alert('For cash sales, total payments must equal the total amount.');
        return;
      }
    } else {
      if (!formData.invoiceDate) {
        alert('Invoice Date is required');
        return;
      }
      if (!items.length) {
        alert('Please add at least one item');
        return;
      }
      if (items.some((i) => !i.productId)) {
        alert('Please select a product for all items');
        return;
      }
    }

    const globalMaxType = company?.maxDiscountType === 'percent' || company?.maxDiscountType === 'amount' ? company.maxDiscountType : null;
    const globalMaxPct = company?.maxDiscountPercent != null ? Number(company.maxDiscountPercent) : null;
    const globalMaxAmt = company?.maxDiscountAmount != null ? Number(company.maxDiscountAmount) : null;
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const isRet = !!item.isReturn;
      if (!isRet) {
        const costPrice = Number(item.costPrice || 0);
        const sellingPrice = Number(item.unitPrice || 0);
        // Validation removed as per request
      }
      const lineMaxType =
        item.maxDiscountType === 'percent' || item.maxDiscountType === 'amount' ? item.maxDiscountType : globalMaxType;
      const lineMaxPct = item.maxDiscountPercent != null ? Number(item.maxDiscountPercent) : globalMaxPct;
      const lineMaxAmt = item.maxDiscountAmount != null ? Number(item.maxDiscountAmount) : globalMaxAmt;
      const discount = toNum(item.discount);
      if (lineMaxType === 'percent' && lineMaxPct != null && lineMaxPct > 0) {
        const qty = toNum(item.quantity);
        const price = toNum(item.unitPrice);
        const lineTotalBeforeDiscount = qty * price;
        if (lineTotalBeforeDiscount > 0 && discount > 0) {
          const lineDiscountPct = (discount / lineTotalBeforeDiscount) * 100;
          if (lineDiscountPct > lineMaxPct) {
            alert(
              `Maximum discount is ${lineMaxPct}%. Line ${i + 1} (${item.productName || 'item'}) has ${lineDiscountPct.toFixed(1)}% discount.`
            );
            return;
          }
        }
      }
      if (lineMaxType === 'amount' && lineMaxAmt != null && lineMaxAmt >= 0 && discount > lineMaxAmt) {
        alert(
          `Maximum discount per line is ${lineMaxAmt} ${currency}. Line ${i + 1} (${item.productName || 'item'}) has discount ${discount}.`
        );
        return;
      }
    }

    try {
      if (mode === 'save-hold') {
        const created = await createHold({
          ...formData,
          invoiceDate: formData.invoiceDate,
          orderDate: formData.orderDate || formData.invoiceDate,
          salesType: formData.salesType,
          creditDays:
            formData.salesType === 'credit'
              ? (formData.creditDays ? parseInt(formData.creditDays, 10) : undefined)
              : undefined,
          salesPersonId: formData.salesPersonId || undefined,
          locationId: formData.locationId || undefined,
          billDiscount: Number(billDiscount) || 0,
          payments: payments.map((p) => ({
            method: p.method,
            paymentAccountId: p.paymentAccountId || undefined,
            referenceNo: p.referenceNo || undefined,
            amount: Number(p.amount || 0),
          })),
          items: items.map((item) => {
            const { vatAmount } = computeLine(item);
            const computedVatPct = toNum(item.vatPercent) || defaultVat;
            const submitUnitPrice = vatIncluded && computedVatPct > 0
              ? parseFloat(item.unitPrice) * (1 + computedVatPct / 100)
              : parseFloat(item.unitPrice);

            return {
              productId: item.productId,
              quantity: parseFloat(item.quantity),
              unit: item.unit || undefined,
              unitPrice: submitUnitPrice,
              discount: toNum(item.discount),
              vatPercent: toNum(item.vatPercent) || defaultVat,
              vatAmount,
              batchNo: item.batchNo || undefined,
              expiryDate: item.expiryDate || undefined,
              serviceData: item.serviceData ? JSON.stringify(item.serviceData) : null,
              serialNumbers: item.serialNumbers || [],
            };
          }),
        }).unwrap();

        if (!sessionUninitialized) refetchSession();

        if (company?.stayOnSalesFormAfterSave && !isView) {
          alert('Sales order placed on hold successfully!');
          resetForm();
          return;
        }

        navigate(created?.id ? `/sales/holds/${created.id}` : '/sales/holds');
        return;
      }
      const created = await createSalesOrder({
        ...formData,
        invoiceNumber: formData.invoiceNumber?.trim() || undefined,
        invoiceDate: formData.invoiceDate,
        orderDate: formData.orderDate || formData.invoiceDate,
        salesType: formData.salesType,
        creditDays:
          formData.salesType === 'credit'
            ? (formData.creditDays ? parseInt(formData.creditDays, 10) : undefined)
            : undefined,
        salesPersonId: formData.salesPersonId || undefined,
        locationId: formData.locationId || undefined,
        billDiscount: Number(billDiscount) || 0,
        payments: payments.map((p) => ({
          method: p.method,
          paymentAccountId: p.paymentAccountId || undefined,
          referenceNo: p.referenceNo || undefined,
          amount: Number(p.amount || 0),
        })),
        items: items.map((item) => {
          const { vatAmount, lineTotal } = computeLine(item);
          const computedVatPct = toNum(item.vatPercent) || defaultVat;
          const submitUnitPrice = vatIncluded && computedVatPct > 0
            ? parseFloat(item.unitPrice) * (1 + computedVatPct / 100)
            : parseFloat(item.unitPrice);

          return {
            productId: item.productId,
            quantity: parseFloat(item.quantity),
            unit: item.unit || undefined,
            unitPrice: submitUnitPrice,
            discount: toNum(item.discount),
            vatPercent: toNum(item.vatPercent) || defaultVat,
            vatAmount,
            batchNo: item.batchNo || undefined,
            expiryDate: item.expiryDate || undefined,
            isReturn: !!item.isReturn,
            isDamaged: !!item.isDamaged,
            damageReason: item.damageReason || undefined,
            serviceData: item.serviceData ? JSON.stringify(item.serviceData) : null,
            serialNumbers: item.serialNumbers || [],
          };
        }),
      }).unwrap();

      if (!sessionUninitialized) refetchSession();

      if (mode === 'save-print' && created?.id) {
        if (company?.enableAutoPrintOnSave) {
          // Trigger print using hidden iframe without redirecting parent window
          const existingIframe = document.getElementById('silent-print-iframe');
          if (existingIframe) {
            existingIframe.remove();
          }
          const iframe = document.createElement('iframe');
          iframe.id = 'silent-print-iframe';
          iframe.style.position = 'absolute';
          iframe.style.left = '-9999px';
          iframe.style.top = '-9999px';
          iframe.style.width = '1024px';
          iframe.style.height = '768px';
          iframe.style.border = 'none';
          iframe.style.opacity = '0';
          iframe.style.pointerEvents = 'none';
          iframe.src = `/sales/orders/${created.id}/print`;
          document.body.appendChild(iframe);

          if (company?.stayOnSalesFormAfterSave && !isView) {
            alert('Sales order created successfully!');
            resetForm();
          } else {
            navigate('/sales');
          }
        } else {
          // If auto-print is disabled, redirect user to the print page for preview
          navigate(`/sales/orders/${created.id}/print`);
        }
      } else {
        if (company?.stayOnSalesFormAfterSave && !isView) {
          alert('Sales order created successfully!');
          resetForm();
        } else {
          navigate('/sales');
        }
      }
    } catch (error) {
      console.log(error);

      alert(error?.data?.message || 'Error creating sales order');
    }
  };

  if (isView && loadingOrder) {
    return <Typography>Loading...</Typography>;
  }

  const isPinActive = Boolean(company?.enableSecurityPin && company?.securityPin);

  if (isView && isPinActive && !viewUnlocked) {
    return (
      <Box sx={{ maxWidth: 460, mx: 'auto', mt: 8, p: 2 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              bgcolor: (theme) => theme.palette.mode === 'dark' ? 'primary.dark' : '#eef2ff',
              color: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2,
            }}
          >
            <LockIcon sx={{ fontSize: 36 }} />
          </Box>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            Invoice Access Restricted
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            This sales invoice is protected by company security PIN. Enter your PIN to view invoice details and amounts.
          </Typography>
          <Button
            variant="contained"
            size="large"
            fullWidth
            startIcon={<LockOpenIcon />}
            onClick={() => setPinPromptOpen(true)}
            sx={{ py: 1.25, borderRadius: 2, fontWeight: 700 }}
          >
            Enter PIN to Unlock
          </Button>
          <Button
            variant="text"
            color="inherit"
            fullWidth
            onClick={() => navigate('/sales')}
            sx={{ mt: 1.5, borderRadius: 2 }}
          >
            Back to Sales List
          </Button>
        </Paper>

        <PinPromptDialog
          open={pinPromptOpen}
          onClose={() => {
            setPinPromptOpen(false);
            if (!viewUnlocked) {
              navigate('/sales');
            }
          }}
          onSuccess={() => {
            setViewUnlocked(true);
            setPinPromptOpen(false);
          }}
          expectedPin={company?.securityPin}
          title="Unlock Sales Invoice"
          description="Enter your security PIN to view this sales invoice."
        />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600, mb: 0 }}>
          {isView ? 'Sales Order Details' : 'New Sales Order'}
        </Typography>
        {!isView && company?.enableSalesSession === true && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Chip
              label={registerOpen ? `Register Open | Opening Balance: ${Number(registerSession?.openingBalance || 0).toFixed(2)} ${currency}` : 'Register Closed'}
              color={registerOpen ? 'success' : 'error'}
              variant="outlined"
              sx={{ fontWeight: 700 }}
            />
            {registerOpen ? (
              <Button
                variant="outlined"
                color="warning"
                size="small"
                onClick={() => setCloseRegisterDialogOpen(true)}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
              >
                Close Register
              </Button>
            ) : (
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={() => setOpenRegisterDialogOpen(true)}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
              >
                Open Register
              </Button>
            )}
          </Box>
        )}
        {isView && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              select
              size="small"
              label="Status"
              value={existingOrder?.status ?? ''}
              onChange={(e) => {
                const newStatus = e.target.value;
                if (newStatus && id) updateSalesOrderStatus({ id, status: newStatus });
              }}
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="quotation">Quotation</MenuItem>
              <MenuItem value="on_hold">On hold</MenuItem>
              <MenuItem value="confirmed">Confirmed</MenuItem>
              <MenuItem value="shipped">Shipped</MenuItem>
              <MenuItem value="delivered">Delivered</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
            </TextField>
            <Button
              variant="outlined"
              startIcon={<PictureAsPdfIcon />}
              onClick={() => {
                if (existingOrder && company) {
                  shareSalesOrderPdf(existingOrder, company, {
                    itemFormat: company.printItemNameFormat || 'name_sku',
                    filename: (existingOrder.invoiceNumber || existingOrder.orderNumber || 'invoice').replace(
                      /\s+/g,
                      '-'
                    ),
                  });
                }
              }}
              disabled={!existingOrder || !company}
              sx={{ borderRadius: 2 }}
            >
              Share
            </Button>
            <Button
              variant="outlined"
              startIcon={<EmailIcon />}
              onClick={() => setEmailDialogOpen(true)}
              disabled={!existingOrder}
              sx={{ borderRadius: 2 }}
            >
              Email
            </Button>
            <Button
              variant="outlined"
              startIcon={<WhatsAppIcon />}
              onClick={() => setWhatsappDialogOpen(true)}
              disabled={!existingOrder}
              sx={{ borderRadius: 2, borderColor: '#25D366', color: '#128C7E', '&:hover': { borderColor: '#128C7E', bgcolor: 'rgba(37, 211, 102, 0.04)' } }}
            >
              WhatsApp
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                if (company?.enableAutoPrintOnSave) {
                  const existingIframe = document.getElementById('silent-print-iframe');
                  if (existingIframe) {
                    existingIframe.remove();
                  }
                  const iframe = document.createElement('iframe');
                  iframe.id = 'silent-print-iframe';
                  iframe.style.position = 'absolute';
                  iframe.style.left = '-9999px';
                  iframe.style.top = '-9999px';
                  iframe.style.width = '1024px';
                  iframe.style.height = '768px';
                  iframe.style.border = 'none';
                  iframe.style.opacity = '0';
                  iframe.style.pointerEvents = 'none';
                  iframe.src = `/sales/orders/${id}/print`;
                  document.body.appendChild(iframe);
                } else {
                  navigate(`/sales/orders/${id}/print`);
                }
              }}
              sx={{ borderRadius: 2 }}
            >
              Print Invoice
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={!existingOrder || existingOrder?.status === 'on_hold' || Number(existingOrder?.balanceAmount || 0) <= 0}
              title={existingOrder?.status === 'on_hold' ? 'Unhold the order first to record payment' : ''}
              onClick={() => {
                const balance = Number(existingOrder?.balanceAmount || 0) || 0;
                setReceivePaymentForm((prev) => ({
                  ...prev,
                  paymentDate: new Date().toISOString().split('T')[0],
                  amount: balance > 0 ? balance.toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2)) : '',
                  paymentMethod: 'cash',
                  paymentAccountId: '',
                  referenceNo: '',
                  notes: '',
                }));
                setReceivePaymentOpen(true);
              }}
              sx={{ borderRadius: 2 }}
            >
              Receive Payment
            </Button>
          </Box>
        )}
      </Box>

      {vanMode && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 2,
            bgcolor: '#E3F2FD',
            border: '1px solid #90CAF9',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <LocalShippingIcon sx={{ color: '#1565C0', fontSize: 28 }} />
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1565C0' }}>
              Van Sales Mode
            </Typography>
            <Typography variant="body2" sx={{ color: '#1565C0' }}>
              Van: {vanName} ({vanPlate}) &bull; Trip: {vanTripNumber} &bull;
              Sales will be made from the van&apos;s stock and automatically linked to this trip.
            </Typography>
          </Box>
        </Paper>
      )}

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <Autocomplete
                  fullWidth
                  options={customerSearchOptions}
                  loading={searchingCustomers}
                  getOptionLabel={(opt) => opt?.name || ''}
                  isOptionEqualToValue={(option, value) => option?.id === value?.id}
                  value={selectedCustomer}
                  onChange={(_, newValue) => {
                    setFormData({ ...formData, customerId: newValue?.id || '' });
                  }}
                  onInputChange={(_, newInputValue, reason) => {
                    if (reason === 'input') {
                      if (newInputValue && newInputValue.length >= 2) {
                        triggerCustomerSearch({ search: newInputValue, limit: 20 });
                      } else if (!newInputValue) {
                        triggerCustomerSearch({ limit: 20 });
                      }
                    }
                  }}
                  onOpen={() => {
                    if (customerSearchOptions.length === 0) triggerCustomerSearch({ limit: 20 });
                  }}
                  disabled={isView}
                  renderInput={(params) => <TextField {...params} label="Customer Name *" required={!formData.customerId} />}
                />
                {!isView && (
                  <>
                    <Button
                      variant="outlined"
                      onClick={() => setCustomerDialogOpen(true)}
                      sx={{ height: 56, minWidth: 44, borderRadius: 1.5 }}
                      title="Add Customer"
                    >
                      <PersonAddAltIcon fontSize="small" />
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleCreateCashCustomer}
                      sx={{
                        height: 56,
                        px: 2,
                        borderRadius: 1.5,
                        backgroundColor: '#4ECDC4',
                        '&:hover': { backgroundColor: '#3BA89F' },
                        color: 'white',
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      + Cash Customer
                    </Button>
                  </>
                )}
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Customer TRN (if VAT registered)"
                value={selectedCustomer?.trn ?? ''}
                InputProps={{ readOnly: true }}
                placeholder="From customer"
              />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Invoice Number"
                name="invoiceNumber"
                value={!isView && !formData.invoiceNumber ? previewInvoiceNumber : formData.invoiceNumber}
                disabled
                helperText={
                  isView
                    ? 'Invoice number is not editable'
                    : 'Auto-generated from Settings on save'
                }
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                type="date"
                label="Invoice Date *"
                name="invoiceDate"
                value={formData.invoiceDate}
                onChange={(e) => setFormData({ ...formData, invoiceDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
                disabled={isView}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                select
                label="Sales Type"
                name="salesType"
                value={formData.salesType}
                onChange={(e) => {
                  const next = e.target.value;
                  setFormData((prev) => ({
                    ...prev,
                    salesType: next,
                    creditDays: next === 'credit' ? prev.creditDays : '',
                  }));
                }}
                disabled={isView}
              >
                <MenuItem value="cash">Cash</MenuItem>
                <MenuItem value="credit">Credit</MenuItem>
              </TextField>
            </Grid>
            {formData.salesType === 'credit' && (
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  select
                  label="Credit days (payment due)"
                  name="creditDays"
                  value={formData.creditDays}
                  onChange={(e) => setFormData({ ...formData, creditDays: e.target.value })}
                  disabled={isView}
                  helperText="Days until payment due"
                >
                  <MenuItem value="">None</MenuItem>
                  {creditDaysOptions.map((days) => (
                    <MenuItem key={days} value={String(days)}>
                      {days} days
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                select
                label="Sales Person (optional)"
                name="salesPersonId"
                value={formData.salesPersonId}
                onChange={(e) => setFormData({ ...formData, salesPersonId: e.target.value })}
                disabled={isView}
              >
                <MenuItem value="">None</MenuItem>
                {userList.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : u.email || u.username}
                  </MenuItem>
                ))}
                {formData.salesPersonId && !userList.find(u => u.id === formData.salesPersonId) && (
                  <MenuItem key={formData.salesPersonId} value={formData.salesPersonId}>
                    Unknown User
                  </MenuItem>
                )}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                select
                label={vanMode ? `Branch / Store (Van: ${vanName})` : 'Branch / Store'}
                name="locationId"
                value={formData.locationId}
                onChange={(e) => setFormData({ ...formData, locationId: e.target.value })}
                disabled={isView || !!vanMode}
              >
                <MenuItem value="">None</MenuItem>
                {locations?.map((loc) => (
                  <MenuItem key={loc.id} value={loc.id}>
                    {loc.name} ({loc.code})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  mb: 2,
                  borderRadius: 3,
                  border: '1px solid #E2E8F0',
                  background: 'linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)',
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05)',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { xs: 'flex-start', md: 'center' },
                    gap: 2,
                  }}
                >
                  {/* Title & Stats */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E293B', fontSize: '1.1rem' }}>
                      Line Items
                    </Typography>
                    <Chip
                      label={`${items.length} ${items.length === 1 ? 'item' : 'items'}`}
                      size="small"
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        bgcolor: '#EEF2F6',
                        color: '#334155',
                      }}
                    />
                    {items.length > 0 && (
                      <Chip
                        label={`${items.reduce((s, it) => s + (Number(it.quantity) || 0), 0)} Qty`}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ fontWeight: 600, fontSize: '0.75rem', height: 24 }}
                      />
                    )}
                  </Box>

                  {/* Item Search Bar & Quick Actions */}
                  {!isView && (
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 1.25,
                        alignItems: 'center',
                        width: { xs: '100%', md: 'auto' },
                        flex: { md: '1 1 560px' },
                        maxWidth: { md: 720 },
                        justifyContent: { md: 'flex-end' },
                      }}
                    >
                      <Autocomplete
                        fullWidth
                        size="medium"
                        autoHighlight
                        freeSolo
                        options={searchingProducts ? [] : (productSearchData?.products || [])}
                        loading={searchingProducts}
                        filterOptions={(x) => x}
                        value={null}
                        inputValue={productSearch}
                        getOptionLabel={(option) => {
                          if (!option) return '';
                          if (typeof option === 'string') return option;
                          const label = formatItemDisplayName(
                            { name: option.name, sku: option.sku || option.barcode, brand: option.brand },
                            itemNameFormat === 'name' ? 'name_sku' : itemNameFormat
                          );
                          return label || '';
                        }}
                        onFocus={() => {
                          if (!productSearchData?.products || productSearchData.products.length === 0) {
                            triggerProductSearch({ limit: 20 });
                          }
                        }}
                        onInputChange={(_, value) => {
                          setProductSearch(value);
                          if (value && value.trim().length >= 1) {
                            triggerProductSearch({ search: value.trim(), limit: 20 });
                          }
                        }}
                        onChange={(_, value) => {
                          if (value && typeof value === 'object') {
                            handleAddProductRow(value);
                            setProductSearch('');
                          }
                        }}
                        slotProps={{
                          paper: {
                            sx: {
                              borderRadius: 2.5,
                              boxShadow: '0 16px 36px -4px rgba(0, 0, 0, 0.18), 0 4px 12px -2px rgba(0, 0, 0, 0.08)',
                              border: '1px solid #CBD5E1',
                              mt: 1,
                              overflow: 'hidden',
                              '& .MuiAutocomplete-listbox': {
                                p: 0.5,
                                maxHeight: 400,
                              },
                            },
                          },
                        }}
                        renderOption={(props, option) => (
                          <ProductSearchOption
                            props={props}
                            option={option}
                            currency={currency}
                            decimals={company?.currencyDecimals ?? 2}
                            isSelected={items.some((it) => String(it.productId) === String(option.id))}
                            priceMode="selling"
                          />
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            id="product-search-input"
                            autoFocus
                            placeholder="Scan barcode or search item (name / SKU / brand)..."
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      width: 30,
                                      height: 30,
                                      borderRadius: 1,
                                      bgcolor: '#F1F5F9',
                                      color: '#475569',
                                      mr: 0.5,
                                    }}
                                  >
                                    <SearchIcon sx={{ fontSize: 18 }} />
                                  </Box>
                                  {params.InputProps.startAdornment}
                                </InputAdornment>
                              ),
                              endAdornment: (
                                <InputAdornment position="end">
                                  {searchingProducts ? (
                                    <CircularProgress color="inherit" size={18} sx={{ mr: 1 }} />
                                  ) : null}
                                  {productSearch ? (
                                    <IconButton
                                      size="small"
                                      onClick={() => setProductSearch('')}
                                      sx={{ mr: 0.5, p: 0.5 }}
                                    >
                                      <ClearIcon fontSize="small" />
                                    </IconButton>
                                  ) : null}
                                  <Chip
                                    label="Enter ↵"
                                    size="small"
                                    sx={{
                                      height: 20,
                                      fontSize: '0.65rem',
                                      bgcolor: '#F1F5F9',
                                      color: '#64748B',
                                      fontWeight: 600,
                                      display: { xs: 'none', sm: 'inline-flex' },
                                    }}
                                  />
                                  {params.InputProps.endAdornment}
                                </InputAdornment>
                              ),
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                const list = searchingProducts ? [] : (productSearchData?.products || []);
                                if (list.length > 0) {
                                  const typed = String(productSearch).trim();
                                  const firstItem = list[0];
                                  if (typed) {
                                    const isExactMatch =
                                      String(firstItem.barcode || '').trim() === typed ||
                                      String(firstItem.sku || '').trim() === typed ||
                                      firstItem.name.toLowerCase().includes(typed.toLowerCase()) ||
                                      (firstItem.brand && firstItem.brand.toLowerCase().includes(typed.toLowerCase()));
                                    if (!isExactMatch) return;
                                  }
                                  handleAddProductRow(firstItem);
                                  setProductSearch('');
                                }
                              }
                            }}
                            sx={{
                              '& .MuiInputBase-root': {
                                height: 50,
                                borderRadius: 2.5,
                                bgcolor: '#FFFFFF',
                                pr: 1,
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  borderColor: '#94A3B8',
                                },
                                '&.Mui-focused': {
                                  boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.12)',
                                },
                              },
                            }}
                          />
                        )}
                      />
                      <Tooltip title="Scan barcode with camera">
                        <Button
                          variant="outlined"
                          startIcon={<QrCodeScannerIcon />}
                          onClick={() => setScanOpen(true)}
                          sx={{
                            height: 50,
                            borderRadius: 2.5,
                            px: 2,
                            whiteSpace: 'nowrap',
                            fontWeight: 600,
                            borderColor: '#CBD5E1',
                            color: '#334155',
                            bgcolor: '#FFFFFF',
                            '&:hover': {
                              borderColor: '#94A3B8',
                              bgcolor: '#F8FAFC',
                            },
                          }}
                        >
                          Scan
                        </Button>
                      </Tooltip>
                      <Tooltip title="Create new product">
                        <Button
                          variant="contained"
                          startIcon={<AddIcon />}
                          onClick={() => setQuickProductOpen(true)}
                          sx={{
                            height: 50,
                            borderRadius: 2.5,
                            px: 2,
                            whiteSpace: 'nowrap',
                            fontWeight: 600,
                            boxShadow: 'none',
                            '&:hover': {
                              boxShadow: '0 4px 12px rgba(37, 99, 235, 0.25)',
                            },
                          }}
                        >
                          New Product
                        </Button>
                      </Tooltip>
                    </Box>
                  )}
                </Box>
              </Paper>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 600, width: 50 }}>S.No</TableCell>
                      <TableCell sx={{ fontWeight: 600, minWidth: 200 }}>Product Name *</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: 140 }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: 100 }} align="center">Qty *</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: 100 }}>Inc. Price</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: 80 }}>Disc.</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: 70 }}>VAT %</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: 90 }}>VAT Amt</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: 100 }}>Total</TableCell>
                      {!isView && <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((item, index) => {
                      const { vatAmount, lineTotalMixed } = computeLine(item);
                      const dec = typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2;
                      return (
                        <TableRow key={index} hover>
                          <TableCell sx={{ fontWeight: 500 }}>{index + 1}</TableCell>
                          <TableCell>
                            <Typography sx={{ fontSize: '0.9rem', fontWeight: 500 }}>
                              {item.productName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {item.productSku} {item.productBrand ? `• ${item.productBrand}` : ''}
                            </Typography>
                            {getCostCode(item.costPrice, company) && (
                              <Typography variant="caption" sx={{ display: 'block', mt: 0.25, fontWeight: 'bold', color: 'info.main' }}>
                                Cost Code: {getCostCode(item.costPrice, company)}
                              </Typography>
                            )}
                            {item.hasWarranty && warrantyPeriodsData && (
                              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'success.main', fontWeight: 600 }}>
                                🛡️ Warranty: {warrantyPeriodsData.find(wp => wp.id === item.warrantyPeriodId)?.name || 'Enabled'}
                              </Typography>
                            )}
                            {item.serialNumbers && Array.isArray(item.serialNumbers) && item.serialNumbers.filter(s => s).length > 0 && (
                              <Typography variant="caption" sx={{ display: 'block', mt: 0.25, color: 'text.secondary' }}>
                                SN: {item.serialNumbers.filter(s => s).join(', ')}
                              </Typography>
                            )}
                            {company?.enableServiceModule && item.productType === 'service' && (
                              <Box sx={{ mt: 1, p: 1.5, borderRadius: 1.5, bgcolor: '#fbf8ff', border: '1px solid #e1d5e7', maxWidth: 400 }}>
                                <Typography variant="caption" color="secondary" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                                  🔧 Service / Repair Details
                                </Typography>
                                <Grid container spacing={1}>
                                  <Grid size={{ xs: 6 }}>
                                    <TextField
                                      size="small"
                                      label="Device Model"
                                      placeholder="e.g. iPhone 14 Pro"
                                      value={item.serviceData?.deviceModel || ''}
                                      disabled={isView}
                                      onChange={(e) => {
                                        const currentServiceData = item.serviceData || {};
                                        handleItemChange(index, 'serviceData', {
                                          ...currentServiceData,
                                          deviceModel: e.target.value
                                        });
                                      }}
                                      slotProps={{ input: { style: { fontSize: '0.75rem' } }, inputLabel: { style: { fontSize: '0.75rem' } } }}
                                      fullWidth
                                    />
                                  </Grid>
                                  <Grid size={{ xs: 6 }}>
                                    <TextField
                                      size="small"
                                      label="IMEI / Serial"
                                      placeholder="IMEI or Serial No"
                                      value={item.serviceData?.imei || ''}
                                      disabled={isView}
                                      onChange={(e) => {
                                        const currentServiceData = item.serviceData || {};
                                        handleItemChange(index, 'serviceData', {
                                          ...currentServiceData,
                                          imei: e.target.value
                                        });
                                      }}
                                      slotProps={{ input: { style: { fontSize: '0.75rem' } }, inputLabel: { style: { fontSize: '0.75rem' } } }}
                                      fullWidth
                                    />
                                  </Grid>
                                  <Grid size={{ xs: 12 }}>
                                    <TextField
                                      size="small"
                                      label="Fault / Complains"
                                      placeholder="Describe issues / diagnostic notes"
                                      value={item.serviceData?.complains || ''}
                                      disabled={isView}
                                      onChange={(e) => {
                                        const currentServiceData = item.serviceData || {};
                                        handleItemChange(index, 'serviceData', {
                                          ...currentServiceData,
                                          complains: e.target.value
                                        });
                                      }}
                                      slotProps={{ input: { style: { fontSize: '0.75rem' } }, inputLabel: { style: { fontSize: '0.75rem' } } }}
                                      fullWidth
                                      multiline
                                      rows={1}
                                    />
                                  </Grid>
                                </Grid>
                              </Box>
                            )}
                            {item.hasWarranty && (
                              <Box sx={{ mt: 1 }}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color={item.serialNumbers?.length === Number(item.quantity) ? "success" : "primary"}
                                  onClick={() => setActiveSerialIndex(index)}
                                >
                                  {item.serialNumbers?.length > 0
                                    ? `Serial Nos: ${item.serialNumbers.length}/${item.quantity}`
                                    : 'Add Serial Numbers'}
                                </Button>
                              </Box>
                            )}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={!!item.isReturn}
                                    onChange={(e) => handleItemChange(index, 'isReturn', e.target.checked)}
                                    disabled={isView}
                                  />
                                }
                                label={<Typography variant="caption" sx={{ fontWeight: item.isReturn ? 600 : 400 }}>Return</Typography>}
                              />
                              {item.isReturn && (
                                <>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        size="small"
                                        color="error"
                                        checked={!!item.isDamaged}
                                        onChange={(e) => handleItemChange(index, 'isDamaged', e.target.checked)}
                                        disabled={isView}
                                      />
                                    }
                                    label={<Typography variant="caption" color="error">Damaged</Typography>}
                                  />
                                  {item.isDamaged && (
                                    <TextField
                                      placeholder="Reason"
                                      size="small"
                                      value={item.damageReason || ''}
                                      onChange={(e) => handleItemChange(index, 'damageReason', e.target.value)}
                                      fullWidth
                                      disabled={isView}
                                      sx={{
                                        '& .MuiInputBase-input': { fontSize: '0.75rem', p: '4px 8px' }
                                      }}
                                    />
                                  )}
                                </>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  const current = Number(item.quantity || 0) || 0;
                                  const next = Math.max(0.01, current - 1);
                                  handleItemChange(index, 'quantity', String(next));
                                }}
                                disabled={isView}
                              >
                                <RemoveIcon fontSize="small" />
                              </IconButton>
                              <TextField
                                id={`qty-input-${index}`}
                                type="number"
                                size="small"
                                value={item.quantity}
                                onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                                sx={{ width: 60 }}
                                inputProps={{ min: 0.01, step: 0.01 }}
                                disabled={isView}
                                required
                              />
                              <IconButton
                                size="small"
                                onClick={() => {
                                  const current = Number(item.quantity || 0) || 0;
                                  const next = Math.max(0.01, current + 1);
                                  handleItemChange(index, 'quantity', String(next));
                                }}
                                disabled={isView}
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <TextField
                              type="number"
                              size="small"
                              value={
                                item.inclusivePriceInput !== undefined
                                  ? item.inclusivePriceInput
                                  : item.unitPrice === ''
                                    ? ''
                                    : Number(
                                      (toNum(item.unitPrice) * (1 + toNum(item.vatPercent) / 100)).toFixed(dec)
                                    ).toString()
                              }
                              onChange={(e) => {
                                const nextInclusive = e.target.value;
                                handleItemChange(index, 'inclusivePriceInput', nextInclusive);
                                if (nextInclusive === '') {
                                  handleItemChange(index, 'unitPrice', '');
                                } else {
                                  const exclusivePrice =
                                    Number(nextInclusive) / (1 + toNum(item.vatPercent) / 100);
                                  handleItemChange(
                                    index,
                                    'unitPrice',
                                    exclusivePrice.toFixed(dec)
                                  );
                                }
                              }}
                              sx={{ width: 90 }}
                              inputProps={{ min: 0, step: 0.01 }}
                              disabled={isView}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              type="number"
                              size="small"
                              value={item.discount}
                              onChange={(e) => handleItemChange(index, 'discount', e.target.value)}
                              sx={{ width: 70 }}
                              inputProps={{ min: 0, step: 0.01 }}
                              disabled={isView}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              type="number"
                              size="small"
                              value={item.vatPercent}
                              onChange={(e) => {
                                handleItemChange(index, 'vatPercent', e.target.value);
                                handleItemChange(index, 'inclusivePriceInput', undefined);
                              }}
                              sx={{ width: 55 }}
                              inputProps={{ min: 0, max: 100, step: 0.01 }}
                              disabled={isView}
                            />
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.85rem' }}>{vatAmount.toFixed(dec)}</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: item.isReturn ? 'error.main' : 'inherit' }}>
                            {lineTotalMixed.toFixed(dec)}
                          </TableCell>
                          {!isView && (
                            <TableCell>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRemoveItem(index)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              {items.length > 0 && (
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 4 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                    Total Qty: {items.reduce((sum, item) => sum + toNum(item.quantity), 0)}
                  </Typography>
                </Box>
              )}
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mt: 2 }}>
                Order Summary
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label={`Subtotal (${currency})`}
                value={orderSubtotal.toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label={`Bill Discount (${currency})`}
                type="number"
                value={billDiscount}
                onChange={(e) => {
                  setBillDiscount(e.target.value);
                  setEditableTotalAmount(null);
                }}
                inputProps={{ min: 0, step: 0.01 }}
                disabled={isView}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label={`Discount Total (${currency})`}
                value={orderDiscountTotal.toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label={`VAT Total (${currency})`}
                value={orderVatTotal.toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label={`Grand Total (${currency})`}
                value={grandTotal.toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}
                InputProps={{ readOnly: true }}
                sx={{ '& input': { fontWeight: 700 } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                type="number"
                disabled={isView}
                label={`Round Off (${currency})`}
                name="roundOff"
                value={formData.roundOff}
                onChange={(e) => {
                  setFormData({ ...formData, roundOff: e.target.value });
                  setEditableTotalAmount(null);
                }}
                inputProps={{ step: 0.01 }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                type="number"
                disabled={isView}
                label={`Total Amount (${currency})`}
                value={
                  editableTotalAmount !== null
                    ? editableTotalAmount
                    : totalAmount.toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))
                }
                onChange={(e) => handleTotalAmountChange(e.target.value)}
                onFocus={() => {
                  setEditableTotalAmount(
                    totalAmount
                      ? Number(totalAmount.toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))).toString()
                      : ''
                  );
                }}
                onBlur={() => {
                  setEditableTotalAmount(null);
                }}
                inputProps={{ step: 0.01 }}
                sx={{
                  '& input': { fontWeight: 700, color: 'primary.main', fontSize: '1.1rem' },
                }}
                helperText={
                  toNum(billDiscount) > 0
                    ? `Discount diff: ${toNum(billDiscount).toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}`
                    : toNum(formData.roundOff) !== 0
                    ? `Round off diff: ${toNum(formData.roundOff) > 0 ? '+' : ''}${toNum(formData.roundOff).toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}`
                    : 'Edit to auto-apply discount / round-off'
                }
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mt: 2 }}>
                Payment
              </Typography>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Method</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Account (optional)</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Reference</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{`Amount (${currency})`}</TableCell>
                      {!isView && <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {payments.map((p, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <TextField
                            select
                            size="small"
                            value={p.method}
                            onChange={(e) => {
                              const next = [...payments];
                              next[index].method = e.target.value;
                              setPayments(next);
                              setAutoPaymentAmount(false);
                            }}
                            sx={{ minWidth: 120 }}
                            disabled={isView}
                          >
                            <MenuItem value="cash">Cash</MenuItem>
                            <MenuItem value="bank">Bank</MenuItem>
                            <MenuItem value="card">Card</MenuItem>
                            <MenuItem value="cheque">Cheque</MenuItem>
                            <MenuItem value="other">Other</MenuItem>
                          </TextField>
                        </TableCell>
                        <TableCell>
                          <TextField
                            select
                            size="small"
                            value={p.paymentAccountId || ''}
                            onChange={(e) => {
                              const next = [...payments];
                              next[index].paymentAccountId = e.target.value;
                              setPayments(next);
                              setAutoPaymentAmount(false);
                            }}
                            sx={{ minWidth: 180 }}
                            disabled={isView}
                          >
                            <MenuItem value="">Auto (by method)</MenuItem>
                            {paymentAccounts?.map((acc) => (
                              <MenuItem key={acc.id} value={acc.id}>
                                {acc.code} - {acc.name}
                              </MenuItem>
                            ))}
                          </TextField>
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            value={p.referenceNo || ''}
                            onChange={(e) => {
                              const next = [...payments];
                              next[index].referenceNo = e.target.value;
                              setPayments(next);
                              setAutoPaymentAmount(false);
                            }}
                            sx={{ minWidth: 140 }}
                            disabled={isView}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            size="small"
                            value={p.amount}
                            onChange={(e) => {
                              const next = [...payments];
                              next[index].amount = e.target.value;
                              setPayments(next);
                              setAutoPaymentAmount(false);
                            }}
                            sx={{ width: 120 }}
                            inputProps={{ min: 0, step: 0.01 }}
                            disabled={isView}
                          />
                        </TableCell>
                        {!isView && (
                          <TableCell>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                setPayments(payments.filter((_, i) => i !== index));
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                    {!isView && (
                      <TableRow>
                        <TableCell colSpan={5}>
                          <Button
                            variant="outlined"
                            onClick={() => {
                              setPayments((prev) => [
                                ...prev,
                                {
                                  method: 'cash',
                                  paymentAccountId: '',
                                  referenceNo: '',
                                  amount: totalAmount.toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2)),
                                },
                              ]);
                              setAutoPaymentAmount(false);
                            }}
                            sx={{ mt: 1 }}
                          >
                            Add Payment Method
                          </Button>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }} />
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label={`Paid Total (${currency})`}
                value={paidTotal.toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label={remaining >= 0 ? `Remaining (${currency})` : `Change (${currency})`}
                value={Math.abs(remaining >= 0 ? remaining : change).toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}
                InputProps={{ readOnly: true }}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Notes"
                name="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                multiline
                rows={2}
                disabled={isView}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  type={isView ? 'button' : 'submit'}
                  variant="contained"
                  disabled={!isView && items.length === 0}
                  onClick={isView ? () => navigate('/sales') : () => { submitModeRef.current = 'save'; }}
                >
                  {isView ? 'Back to Sales' : 'Create Order'}
                </Button>
                {!isView && (
                  <>
                    <Button type="submit" variant="outlined" color="warning" onClick={() => { submitModeRef.current = 'save-hold'; }}>
                      Create &amp; Hold
                    </Button>
                    <Button type="submit" variant="outlined" onClick={() => { submitModeRef.current = 'save-print'; }}>
                      Save &amp; Print
                    </Button>
                    <Button variant="outlined" onClick={() => setCancelDialogOpen(true)}>
                      Cancel
                    </Button>
                  </>
                )}
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
      <Dialog
        open={customerDialogOpen}
        onClose={() => setCustomerDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 1.5, boxShadow: '0 12px 40px rgba(0,0,0,0.15)' } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Add Customer</DialogTitle>
        <DialogContent
          sx={{
            pt: 2,
            overflow: 'visible',
            '& .MuiInputLabel-root': { overflow: 'visible' },
          }}
        >
          <CustomerFormContent
            compact
            onCancel={() => setCustomerDialogOpen(false)}
            onSuccess={(created) => {
              setCustomerDialogOpen(false);
              if (created?.id) {
                setCustomerSearchOptions(prev => [created, ...prev]);
                setFormData((prev) => ({ ...formData, customerId: created.id }));
              }
            }}
          />
        </DialogContent>
      </Dialog>
      <Dialog
        open={receivePaymentOpen}
        onClose={() => setReceivePaymentOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 1.5 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Receive Payment</DialogTitle>
        <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            type="date"
            label="Payment Date *"
            value={receivePaymentForm.paymentDate}
            onChange={(e) =>
              setReceivePaymentForm((prev) => ({ ...prev, paymentDate: e.target.value }))
            }
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            select
            label="Method *"
            value={receivePaymentForm.paymentMethod}
            onChange={(e) =>
              setReceivePaymentForm((prev) => ({ ...prev, paymentMethod: e.target.value }))
            }
            fullWidth
          >
            <MenuItem value="cash">Cash</MenuItem>
            <MenuItem value="bank">Bank</MenuItem>
            <MenuItem value="card">Card</MenuItem>
            <MenuItem value="cheque">Cheque</MenuItem>
          </TextField>
          <TextField
            select
            label="Account (optional)"
            value={receivePaymentForm.paymentAccountId}
            onChange={(e) =>
              setReceivePaymentForm((prev) => ({
                ...prev,
                paymentAccountId: e.target.value,
              }))
            }
            fullWidth
          >
            <MenuItem value="">Auto (by method)</MenuItem>
            {paymentAccounts?.map((acc) => (
              <MenuItem key={acc.id} value={acc.id}>
                {acc.code} - {acc.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            type="number"
            label={`Amount (${currency}) *`}
            value={receivePaymentForm.amount}
            onChange={(e) =>
              setReceivePaymentForm((prev) => ({ ...prev, amount: e.target.value }))
            }
            fullWidth
            inputProps={{ min: 0, step: 0.01 }}
          />
          <TextField
            label="Reference"
            value={receivePaymentForm.referenceNo}
            onChange={(e) =>
              setReceivePaymentForm((prev) => ({ ...prev, referenceNo: e.target.value }))
            }
            fullWidth
          />
          <TextField
            label="Notes"
            value={receivePaymentForm.notes}
            onChange={(e) =>
              setReceivePaymentForm((prev) => ({ ...prev, notes: e.target.value }))
            }
            fullWidth
            multiline
            rows={2}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1, pb: 1 }}>
            <Button onClick={() => setReceivePaymentOpen(false)} disabled={recordingPayment}>
              Close
            </Button>
            <Button
              variant="contained"
              disabled={recordingPayment}
              onClick={async () => {
                if (!existingOrder?.id) return;
                const amt = Number(receivePaymentForm.amount || 0);
                if (!amt || amt <= 0) {
                  alert('Amount must be greater than zero');
                  return;
                }
                try {
                  await recordSalesPayment({
                    id: existingOrder.id,
                    paymentDate: receivePaymentForm.paymentDate,
                    amount: amt,
                    paymentMethod: receivePaymentForm.paymentMethod,
                    paymentAccountId:
                      receivePaymentForm.paymentAccountId || undefined,
                    referenceNo: receivePaymentForm.referenceNo || undefined,
                    notes: receivePaymentForm.notes || undefined,
                  }).unwrap();
                  setReceivePaymentOpen(false);
                } catch (err) {
                  alert(err?.data?.message || 'Error recording payment');
                }
              }}
            >
              {recordingPayment ? 'Saving...' : 'Save Payment'}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog open={scanOpen} onClose={() => setScanOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Scan Item Barcode</DialogTitle>
        <DialogContent>
          {scanError ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {scanError}
            </Alert>
          ) : (
            <Alert severity="info" sx={{ mb: 2 }}>
              Point the camera at the barcode. The item will be added automatically when matched.
            </Alert>
          )}

          {scanOpen && (
            <BarcodeScannerComponent
              width="100%"
              height={360}
              delay={200}
              constraints={{ facingMode: 'environment' }}
              onUpdate={(err, result) => {
                if (err) {
                  if (err?.name === 'NotAllowedError') {
                    setScanError('Camera permission denied. Please allow camera access and try again.');
                  } else if (err?.name !== 'NotFoundException') {
                    setScanError('Unable to read barcode. Try better lighting or move closer.');
                  }
                  return;
                }
                const text = result?.text ? String(result.text).trim() : '';
                if (!text) return;

                setProductSearch(text);
                setPendingBarcode(text);
                triggerProductSearch({ search: text, limit: 20 });
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setScanOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Cancel Entry?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to cancel? Any unsaved changes will be lost.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setCancelDialogOpen(false)}>
            No
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              setCancelDialogOpen(false);
              window.history.back();
              setTimeout(() => navigate('/sales'), 100);
            }}
          >
            Yes, Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={duplicateDialog.open}
        onClose={() => setDuplicateDialog({ open: false, existingIndex: -1, product: null })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Item already added</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1.5 }}>
            {duplicateDialog.product
              ? `“${formatItemDisplayName(
                {
                  name: duplicateDialog.product.name,
                  sku: duplicateDialog.product.sku || duplicateDialog.product.barcode,
                  brand: duplicateDialog.product.brand,
                },
                itemNameFormat
              )}” is already in the list.`
              : 'This item is already in the list.'}
          </Typography>
          <Typography color="text.secondary">Do you want to increase the quantity by 1?</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => setDuplicateDialog({ open: false, existingIndex: -1, product: null })}
          >
            No
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              const idx = duplicateDialog.existingIndex;
              setItems((prev) => {
                if (idx < 0 || idx >= prev.length) return prev;
                const next = [...prev];
                const currentQty = Number(next[idx]?.quantity || 0) || 0;
                next[idx] = {
                  ...next[idx],
                  quantity: String(Math.max(0.01, currentQty + 1)),
                };
                return next;
              });
              setDuplicateDialog({ open: false, existingIndex: -1, product: null });
              setProductSearch('');
              setPendingBarcode('');
              setScanOpen(false);
            }}
          >
            Yes, increase
          </Button>
        </DialogActions>
      </Dialog>
      {/* Email Dialog */}
      <Dialog open={emailDialogOpen} onClose={() => !emailLoading && setEmailDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 600 }}>Send Invoice via Email</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Recipient Email"
                value={emailRecipient}
                onChange={(e) => setEmailRecipient(e.target.value)}
                placeholder="customer@example.com"
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                select
                label="Select Template"
                value={selectedEmailTemplate}
                onChange={(e) => setSelectedEmailTemplate(e.target.value)}
                helperText="Leave empty to use default template"
              >
                <MenuItem value=""><em>Default Template</em></MenuItem>
                {Array.isArray(emailTemplates) &&
                  emailTemplates
                    .filter(t => t.type === 'sales_invoice' || (existingOrder?.status === 'quotation' && t.type === 'quotation'))
                    .map((t) => (
                      <MenuItem key={t.id} value={t.id}>
                        {t.name}
                      </MenuItem>
                    ))
                }
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailDialogOpen(false)} disabled={emailLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSendEmail}
            disabled={emailLoading || !emailRecipient}
            startIcon={emailLoading ? <CircularProgress size={20} /> : <EmailIcon />}
          >
            {emailLoading ? 'Sending...' : 'Send Email'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* WhatsApp Dialog */}
      <Dialog
        open={whatsappDialogOpen}
        onClose={() => !whatsappLoading && setWhatsappDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <WhatsAppIcon sx={{ color: '#25D366' }} />
          Share via WhatsApp
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            Send the invoice PDF directly to the customer's WhatsApp.
          </Typography>
          <TextField
            fullWidth
            label="WhatsApp Number (with country code)"
            placeholder="e.g. 971501234567"
            value={whatsappPhone}
            onChange={(e) => setWhatsappPhone(e.target.value)}
            disabled={whatsappLoading}
            sx={{ mb: 1.5 }}
            helperText="Include country code without + or 00 prefix."
          />
        </DialogContent>
        <DialogActions>
          <Button disabled={whatsappLoading} onClick={() => setWhatsappDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            disabled={whatsappLoading || !whatsappPhone}
            variant="contained"
            onClick={handleSendWhatsapp}
            sx={{ bgcolor: '#25D366', '&:hover': { bgcolor: '#128C7E' }, minWidth: 140 }}
            startIcon={whatsappLoading ? <CircularProgress size={20} /> : <WhatsAppIcon />}
          >
            {whatsappLoading ? 'Processing...' : 'Send WhatsApp'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Open Register Dialog */}
      <Dialog
        open={openRegisterDialogOpen}
        onClose={() => registerOpen && setOpenRegisterDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Open Cash Register Session</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            Please open your register session by entering the starting cash balance.
          </Typography>
          <TextField
            fullWidth
            type="number"
            label="Opening Cash Balance"
            placeholder="e.g. 100.00"
            value={openingBalanceInput}
            onChange={(e) => setOpeningBalanceInput(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          {registerOpen && (
            <Button onClick={() => setOpenRegisterDialogOpen(false)}>
              Cancel
            </Button>
          )}
          <Button
            variant="contained"
            onClick={handleOpenRegister}
            disabled={!openingBalanceInput}
            sx={{ fontWeight: 600 }}
          >
            Open Register
          </Button>
        </DialogActions>
      </Dialog>

      {/* Close Register Dialog */}
      <Dialog
        open={closeRegisterDialogOpen}
        onClose={() => setCloseRegisterDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Close Register & Shift Summary</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mb: 3 }}>
            <Grid container spacing={2}>
              <Grid size={{xs:6}}>
                <Typography variant="caption" color="text.secondary">Opened At</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{registerSession?.openedAt}</Typography>
              </Grid>
              <Grid size={{xs:6}}>
                <Typography variant="caption" color="text.secondary">Opening Balance</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{Number(registerSession?.openingBalance || 0).toFixed(2)} {currency}</Typography>
              </Grid>
              <Grid size={{xs:6}}>
                <Typography variant="caption" color="text.secondary">Sales Count</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{registerSession?.salesCount || 0}</Typography>
              </Grid>
              <Grid size={{xs:6}}>
                <Typography variant="caption" color="text.secondary">Total Sales</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{Number(registerSession?.salesTotal || 0).toFixed(2)} {currency}</Typography>
              </Grid>
              <Grid size={{xs:12}}>
                <Typography variant="caption" color="text.secondary">Expected Cash in Register</Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'primary.main' }}>
                  {(Number(registerSession?.openingBalance || 0) + Number(registerSession?.salesTotal || 0)).toFixed(2)} {currency}
                </Typography>
              </Grid>
            </Grid>
          </Box>
          <TextField
            fullWidth
            type="number"
            label="Actual Closing Cash Balance"
            placeholder="e.g. 500.00"
            value={closingBalanceInput}
            onChange={(e) => setClosingBalanceInput(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            type="number"
            label="Petty Cash Kept in Drawer"
            placeholder="e.g. 100.00"
            value={closingPettyCashInput}
            onChange={(e) => setClosingPettyCashInput(e.target.value)}
            sx={{ mb: 1 }}
          />
          {closingBalanceInput && (() => {
            const expected = Number(registerSession?.openingBalance || 0) + Number(registerSession?.salesTotal || 0);
            const actual = parseFloat(closingBalanceInput) || 0;
            const petty = parseFloat(closingPettyCashInput) || 0;
            const diff = actual - expected;
            const netTransfer = Math.max(0, actual - petty);
            return (
              <Box sx={{ mt: 1.5 }}>
                <Alert severity={diff === 0 ? 'success' : 'warning'} sx={{ mb: 1.5 }}>
                  Difference: {diff > 0 ? '+' : ''}{diff.toFixed(2)} {currency}
                  {diff === 0 ? ' (Perfectly Reconciled!)' : (diff > 0 ? ' (Cash Surplus)' : ' (Cash Shortage)')}
                </Alert>
                <Alert severity="info">
                  Net Cash Transfer to Safe: {netTransfer.toFixed(2)} {currency}
                </Alert>
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloseRegisterDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleCloseRegister}
            disabled={!closingBalanceInput}
            sx={{ fontWeight: 600 }}
          >
            Close Register & Save
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={activeSerialIndex !== null}
        onClose={() => setActiveSerialIndex(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Enter Serial Numbers</DialogTitle>
        <DialogContent>
          {activeSerialIndex !== null && items[activeSerialIndex] && (() => {
            const item = items[activeSerialIndex];
            const qty = Math.max(1, Math.floor(Number(item.quantity || 1)));
            const currentSerials = Array.isArray(item.serialNumbers) ? [...item.serialNumbers] : [];
            while (currentSerials.length < qty) currentSerials.push('');
            if (currentSerials.length > qty) currentSerials.length = qty;

            if (activeSerialScanIndex !== null) {
              return (
                <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Scanning Serial Number {activeSerialScanIndex + 1}
                  </Typography>
                  <BarcodeScannerComponent
                    width="100%"
                    height={300}
                    delay={200}
                    constraints={{ facingMode: 'environment' }}
                    onUpdate={(err, result) => {
                      if (err) return;
                      const text = result?.text ? String(result.text).trim() : '';
                      if (!text) return;

                      const newSerials = [...currentSerials];
                      newSerials[activeSerialScanIndex] = text;
                      handleItemChange(activeSerialIndex, 'serialNumbers', newSerials);
                      
                      const nextIndex = newSerials.findIndex((s, i) => i > activeSerialScanIndex && !s);
                      if (nextIndex !== -1) {
                         setActiveSerialScanIndex(nextIndex);
                      } else {
                         setActiveSerialScanIndex(null);
                      }
                    }}
                  />
                  <Button variant="outlined" onClick={() => setActiveSerialScanIndex(null)}>Cancel Scan</Button>
                </Box>
              );
            }

            return (
              <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Enter {qty} unique serial numbers for {item.productName}. You can type or scan them.
                </Typography>
                {currentSerials.map((serial, idx) => (
                  <TextField
                    key={idx}
                    label={`Serial Number ${idx + 1}`}
                    value={serial}
                    size="small"
                    fullWidth
                    onChange={(e) => {
                      const newSerials = [...currentSerials];
                      newSerials[idx] = e.target.value;
                      handleItemChange(activeSerialIndex, 'serialNumbers', newSerials);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const nextInput = document.getElementById(`serial-input-${idx + 1}`);
                        if (nextInput) nextInput.focus();
                      }
                    }}
                    id={`serial-input-${idx}`}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="Scan serial"
                            edge="end"
                            onClick={() => setActiveSerialScanIndex(idx)}
                          >
                            <QrCodeScannerIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                ))}
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActiveSerialIndex(null)} variant="contained">Done</Button>
        </DialogActions>
      </Dialog>
      
      <QuickProductDialog
        open={quickProductOpen}
        onClose={() => setQuickProductOpen(false)}
        onCreated={(newProduct) => {
          handleAddProductRow(newProduct);
        }}
      />
    </Box>
  );
}

export default SalesOrderForm;

